import React from "react";
import DownPaymentInput from "Shared/CurrencyInput/DownPaymentInput";
import CellChangeIndicator from "src/components/MyGarage/TableCompare/Cells/CellChangeIndicator";
import { AccessoriesState } from "src/store/reducers/accessories";
import { VehicleState } from "Types/state";
import CellDropdown from "./Cells/CellDropdown";
import { formatValue, getNestedProperty, getPaymentType, performTemplateReplacementOnKey } from "./lib";
import { DisabledCell } from "./shared";

interface VehicleFeaturesProps {
    featureList: any;
    onUpdate: any;
    vehicle: Readonly<VehicleState>;
    vehicleAccessories: AccessoriesState;
}

export interface BaseCellProps {
    dataKey: string;
    value: any;
    vin: string;
    paymentType: string;
    onUpdate: (vin: string, key: string, value: any) => void;
    title?: string;
}

const VehicleFeatures = ({ vehicle, featureList, onUpdate, vehicleAccessories }: VehicleFeaturesProps) => {
    const renderCellType = (CellType: any, feature: any) => {
        if (!vehicle || (vehicle && vehicle.loading)) {
            return null;
        }
        const paymentType = getPaymentType(vehicle);
        const disableList =
            typeof feature.disable === "function"
                ? feature.disable(vehicle, vehicleAccessories)
                : feature.disable || [];
        if (disableList.length > 0 && disableList.includes(paymentType)) {
            return <DisabledCell testid={`${vehicle.vin}:${feature.id}`} />;
        }

        const cleanKey = performTemplateReplacementOnKey(vehicle, feature.dataKey);

        const value = feature.dataFunction
            ? feature.dataFunction(vehicle)
            : formatValue(getNestedProperty(vehicle, cleanKey), feature.format);

        let additionalProps = {} as any;

        switch (CellType) {
            case CellDropdown:
                additionalProps = {
                    options: feature.options(vehicle),
                    selected: value,
                };
                break;
            case DownPaymentInput:
                additionalProps = {
                    vehicle,
                };
                break;
            default:
                additionalProps = {};
                break;
        }

        const onUpdateWithFormat = (featureFormatValue: any) => (vin: string, key: string, returnedValue: any) => {
            return onUpdate(vin, key, returnedValue, featureFormatValue);
        };

        if (feature.id !== "Frequency") {
            additionalProps = { ...additionalProps, onUpdate: onUpdateWithFormat(feature.valueFormat) };
        }

        return (
            <CellType
                dataKey={cleanKey}
                value={value}
                vin={vehicle.vin}
                paymentType={paymentType}
                title={typeof feature.label === "function" ? feature.label([vehicle]) : feature.label}
                {...additionalProps}
            />
        );
    };

    return featureList.map((feature: any) => {
        const vin = vehicle?.vin;
        const type = vehicle?.payments?.dealer ? "dealer" : vehicle?.payments?.selected;
        const offer = vehicle?.payments?.[vehicle?.payments?.selected ?? "loan"]?.selected_offer;

        return (
            <CellChangeIndicator
                key={feature.id}
                featureId={feature.id}
                hasChanged={vehicle && !vehicle.loading && feature.hasChanged && feature.hasChanged(vin, type, offer)}
            >
                {renderCellType(feature.cellType, feature)}
            </CellChangeIndicator>
        );
    });
};

export default VehicleFeatures;
